import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import * as api from '../../services/api';
import styles from './NotificationDropdown.module.css';

export default function NotificationDropdown() {
    const { t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch unread count on mount and periodically
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000); // Check every minute
        return () => clearInterval(interval);
    }, []);

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const result = await api.getUnreadCount();
            if (result.success) {
                setUnreadCount(result.data?.count || 0);
            }
        } catch (err) {
            console.error('Failed to fetch unread count:', err);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const result = await api.getNotifications({ limit: 10 });
            if (result.success) {
                setNotifications(result.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await api.markAsRead(notificationId);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await api.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'approval':
                return '✅';
            case 'rejection':
                return '❌';
            case 'comment':
                return '💬';
            case 'like':
                return '❤️';
            case 'favorite':
                return '⭐';
            default:
                return '🔔';
        }
    };

    const getNotificationMessage = (notification) => {
        const { type, data } = notification;
        switch (type) {
            case 'approval':
                return t('notifications.approved') || 'Your configuration has been approved!';
            case 'rejection':
                return `${t('notifications.rejected') || 'Your configuration was rejected.'} ${data?.reason ? `Reason: ${data.reason}` : ''}`;
            case 'comment':
                return `${data?.userName || 'Someone'} ${t('notifications.commented') || 'commented on your configuration'}`;
            case 'like':
                return `${data?.userName || 'Someone'} ${t('notifications.liked') || 'liked your configuration'}`;
            case 'favorite':
                return `${data?.userName || 'Someone'} ${t('notifications.favorited') || 'saved your configuration'}`;
            default:
                return notification.message || t('notifications.newNotification') || 'New notification';
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('notifications.justNow') || 'Just now';
        if (diffMins < 60) return `${diffMins}${t('notifications.minsAgo') || 'm ago'}`;
        if (diffHours < 24) return `${diffHours}${t('notifications.hoursAgo') || 'h ago'}`;
        if (diffDays < 7) return `${diffDays}${t('notifications.daysAgo') || 'd ago'}`;
        return date.toLocaleDateString();
    };

    return (
        <div className={styles.notificationWrapper} ref={dropdownRef}>
            <button
                className={styles.notificationBtn}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Notifications"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span className={styles.badge}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.dropdownHeader}>
                        <h3>{t('notifications.title') || 'Notifications'}</h3>
                        {unreadCount > 0 && (
                            <button
                                className={styles.markAllBtn}
                                onClick={handleMarkAllAsRead}
                            >
                                {t('notifications.markAllRead') || 'Mark all read'}
                            </button>
                        )}
                    </div>

                    <div className={styles.notificationList}>
                        {loading ? (
                            <div className={styles.loading}>
                                {t('common.loading') || 'Loading...'}
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className={styles.empty}>
                                <span className={styles.emptyIcon}>🔔</span>
                                <p>{t('notifications.empty') || 'No notifications yet'}</p>
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''}`}
                                    onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                                >
                                    <span className={styles.notificationIcon}>
                                        {getNotificationIcon(notification.type)}
                                    </span>
                                    <div className={styles.notificationContent}>
                                        <p className={styles.notificationMessage}>
                                            {getNotificationMessage(notification)}
                                        </p>
                                        {notification.configuration_id && (
                                            <Link
                                                to={`/gallery/${notification.configuration_id}`}
                                                className={styles.notificationLink}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsOpen(false);
                                                }}
                                            >
                                                {t('notifications.viewConfig') || 'View configuration'}
                                            </Link>
                                        )}
                                        <span className={styles.notificationTime}>
                                            {formatTime(notification.created_at)}
                                        </span>
                                    </div>
                                    {!notification.read && (
                                        <span className={styles.unreadDot} />
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <Link
                            to="/profile?tab=notifications"
                            className={styles.viewAllBtn}
                            onClick={() => setIsOpen(false)}
                        >
                            {t('notifications.viewAll') || 'View all notifications'}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}
